/**
 * Endpoints de **Usuarios** (`/api/usuarios`).
 *
 * Entran los grupos `admin` y `users`; además la API exige el permiso específico
 * de cada acción (`usuarios.crear`, `usuarios.editar`, `usuarios.cambiar_estado`,
 * `usuarios.asignar_grupos`, `usuarios.asignar_permisos`), salvo el superadmin.
 *
 * La foto viaja en binario (`POST /api/usuarios/:id/foto`) porque el cliente HTTP
 * envía JSON, y se muestra con `urlFotoUsuario()`, que apunta a la ruta pública.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { env } from '@/config/env';
import { apiClient } from '@/lib/http/api-client';
import { getSessionProvider } from '@/lib/http/session-provider';

import type {
  CatalogosUsuarios,
  PermisosUsuarios,
  Usuario,
  UsuarioPayload,
  UsuariosRespuesta,
} from './usuarios.types';

export const usuariosKeys = {
  lista: (estado: string, buscar: string) => ['operaciones', 'usuarios', estado, buscar] as const,
  detalle: (id: number) => ['operaciones', 'usuarios', id] as const,
  catalogos: ['operaciones', 'usuarios', 'catalogos'] as const,
} as const;

/** URL de la foto de un usuario (ruta pública, sin token). */
export function urlFotoUsuario(id: number, version: string | null): string {
  const sufijo = version ? `?v=${encodeURIComponent(version)}` : '';

  return `${env.apiUrl}/api/usuarios/foto/${id}${sufijo}`;
}

export const usuariosApi = {
  listar: (estado: string, buscar: string, signal?: AbortSignal): Promise<UsuariosRespuesta> => {
    const parametros = new URLSearchParams();
    if (estado === '0' || estado === '1') parametros.set('estado', estado);
    if (buscar.trim() !== '') parametros.set('buscar', buscar.trim());

    const consulta = parametros.toString();

    return apiClient.get<UsuariosRespuesta>(`/api/usuarios${consulta ? `?${consulta}` : ''}`, {
      signal,
    });
  },
  catalogos: (signal?: AbortSignal): Promise<CatalogosUsuarios> =>
    apiClient.get<CatalogosUsuarios>('/api/usuarios/catalogos', { signal }),
  detalle: (id: number, signal?: AbortSignal): Promise<{ usuario: Usuario; permisos: number[] }> =>
    apiClient.get<{ usuario: Usuario; permisos: number[] }>(`/api/usuarios/${id}`, { signal }),
  crear: (payload: UsuarioPayload): Promise<{ usuario: Usuario; mensaje: string }> =>
    apiClient.post<{ usuario: Usuario; mensaje: string }>('/api/usuarios', { body: payload }),
  actualizar: (
    id: number,
    payload: UsuarioPayload,
  ): Promise<{ usuario: Usuario; mensaje: string }> =>
    apiClient.put<{ usuario: Usuario; mensaje: string }>(`/api/usuarios/${id}`, { body: payload }),
  cambiarEstado: (id: number, activo: boolean): Promise<{ mensaje: string }> =>
    apiClient.patch<{ mensaje: string }>(`/api/usuarios/${id}/estado`, { body: { activo } }),
};

/** Sube la foto de un usuario (cuerpo binario, con el token de la sesión). */
export async function subirFotoUsuario(
  id: number,
  archivo: File,
): Promise<{ foto: string; version: string }> {
  const token = getSessionProvider()?.getAccessToken();
  const respuesta = await fetch(`${env.apiUrl}/api/usuarios/${id}/foto`, {
    method: 'POST',
    headers: {
      'Content-Type': archivo.type || 'image/png',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: archivo,
  });

  const contenido = (await respuesta.json().catch(() => null)) as
    | { status: boolean; response?: { foto: string; version: string }; error?: string }
    | null;

  if (!respuesta.ok || !contenido?.status || !contenido.response) {
    throw new Error(contenido?.error ?? 'No se pudo subir la foto.');
  }

  return contenido.response;
}

/** Listado de usuarios con su filtro de estado y búsqueda. */
export function useUsuarios(estado: string, buscar: string) {
  return useQuery({
    queryKey: usuariosKeys.lista(estado, buscar),
    queryFn: ({ signal }) => usuariosApi.listar(estado, buscar, signal),
  });
}

/** Catálogos (grupos y permisos específicos) para el formulario. */
export function useCatalogosUsuarios() {
  return useQuery({
    queryKey: usuariosKeys.catalogos,
    queryFn: ({ signal }) => usuariosApi.catalogos(signal),
  });
}

/** Detalle de un usuario (datos + permisos específicos), sólo si hay id. */
export function useUsuario(id: number | null) {
  return useQuery({
    queryKey: usuariosKeys.detalle(id ?? 0),
    queryFn: ({ signal }) => usuariosApi.detalle(id ?? 0, signal),
    enabled: id !== null,
  });
}

/** Alta, edición, estado y foto de usuarios. */
export function useMutarUsuarios() {
  const cliente = useQueryClient();
  const refrescar = () => {
    void cliente.invalidateQueries({ queryKey: ['operaciones', 'usuarios'] });
  };

  return {
    crear: useMutation({ mutationFn: usuariosApi.crear, onSuccess: refrescar }),
    actualizar: useMutation({
      mutationFn: ({ id, payload }: { id: number; payload: UsuarioPayload }) =>
        usuariosApi.actualizar(id, payload),
      onSuccess: refrescar,
    }),
    cambiarEstado: useMutation({
      mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
        usuariosApi.cambiarEstado(id, activo),
      onSuccess: refrescar,
    }),
    subirFoto: useMutation({
      mutationFn: ({ id, archivo }: { id: number; archivo: File }) => subirFotoUsuario(id, archivo),
      onSuccess: refrescar,
    }),
  };
}

export type { PermisosUsuarios };
